import React from 'react';

export type InlineImageTextSegment =
  | { type: 'text'; value: string }
  | { type: 'image'; url: string; alt: string; widthPx?: number; heightPx?: number; verticalOffsetEm?: number }
  | { type: 'sizedText'; value: string; sizeEm?: number; sizePx?: number; bold?: boolean; italic?: boolean; color?: string };

const INLINE_MARKDOWN_IMAGE_REGEX = /!\[([^\]]*)\]\(([^)]+)\)/g;
const INLINE_SIZED_TEXT_REGEX = /\{\{([^{}|]+?)(?:\|([^{}]*))?\}\}/g;
const INLINE_IMAGE_DIMENSION_MIN_PX = 8;
const INLINE_IMAGE_DIMENSION_MAX_PX = 1024;
const INLINE_TEXT_SCALE_MIN = 0.4;
const INLINE_TEXT_SCALE_MAX = 3;
const INLINE_TEXT_SIZE_MIN_PX = 8;
const INLINE_TEXT_SIZE_MAX_PX = 240;

const clampInlineImageDimension = (value: number): number =>
  Math.min(INLINE_IMAGE_DIMENSION_MAX_PX, Math.max(INLINE_IMAGE_DIMENSION_MIN_PX, Math.round(value)));

const clampInlineTextScale = (value: number): number =>
  Math.min(INLINE_TEXT_SCALE_MAX, Math.max(INLINE_TEXT_SCALE_MIN, value));

const clampInlineTextPx = (value: number): number =>
  Math.min(INLINE_TEXT_SIZE_MAX_PX, Math.max(INLINE_TEXT_SIZE_MIN_PX, Math.round(value)));

const isSafeInlineImageUrl = (rawUrl: string): boolean => {
  const trimmedUrl = rawUrl.trim();
  if (!trimmedUrl) return false;
  if (trimmedUrl.startsWith('data:image/')) return true;

  try {
    const parsedUrl = new URL(trimmedUrl, 'https://example.com');
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
  } catch {
    return false;
  }
};

const normalizeInlineImageUrl = (rawUrl: string): string =>
  rawUrl
    .replace(/^<|>$/g, '')
    .replace(/\s+/g, '')
    .trim();

const parseInlineImageMeta = (rawMeta: string): { alt: string; widthPx?: number; heightPx?: number; verticalOffsetEm?: number } => {
  const parts = rawMeta
    .split('|')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const [altText = '', ...optionParts] = parts;
  const parsedMeta: { alt: string; widthPx?: number; heightPx?: number; verticalOffsetEm?: number } = { alt: altText };

  optionParts.forEach((option) => {
    const [rawKey, rawValue] = option.split('=').map((part) => part.trim());
    if (!rawKey || !rawValue) return;
    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) return;
    const boundedValue = clampInlineImageDimension(numericValue);
    const normalizedKey = rawKey.toLowerCase();

    if (normalizedKey === 'w' || normalizedKey === 'width') {
      parsedMeta.widthPx = boundedValue;
    }
    if (normalizedKey === 'h' || normalizedKey === 'height' || normalizedKey === 'size') {
      parsedMeta.heightPx = boundedValue;
    }
    if (normalizedKey === 't' || normalizedKey === 'top') {
      const floatVal = parseFloat(rawValue);
      if (!isNaN(floatVal)) parsedMeta.verticalOffsetEm = floatVal;
    }
  });

  return parsedMeta;
};

const SAFE_COLOR_REGEX = /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]+|rgba?\([0-9.,\s%]+\)|hsla?\([0-9.,\s%]+\))$/;

const parseInlineSizedTextMeta = (rawMeta: string): { sizeEm?: number; sizePx?: number; bold?: boolean; italic?: boolean; color?: string } => {
  const options = rawMeta
    .split('|')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const parsed: { sizeEm?: number; sizePx?: number; bold?: boolean; italic?: boolean; color?: string } = {};

  options.forEach((option) => {
    const [rawKey, rawValue] = option.includes('=')
      ? option.split('=').map((part) => part.trim())
      : ['s', option];
    if (!rawKey || !rawValue) return;

    const normalizedKey = rawKey.toLowerCase();
    const normalizedValue = rawValue.toLowerCase();

    const parseNumeric = (value: string): number | null => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    const parsePx = (value: string): number | null => {
      const stripped = value.toLowerCase().endsWith('px') ? value.slice(0, -2).trim() : value;
      return parseNumeric(stripped);
    };

    if (normalizedKey === 'px' || normalizedKey === 'sizepx' || normalizedKey === 'fontpx') {
      const pxValue = parsePx(rawValue);
      if (pxValue !== null) parsed.sizePx = clampInlineTextPx(pxValue);
      return;
    }

    if (normalizedKey === 's' || normalizedKey === 'size' || normalizedKey === 'scale' || normalizedKey === 'em') {
      if (normalizedValue.endsWith('px')) {
        const pxValue = parsePx(rawValue);
        if (pxValue !== null) parsed.sizePx = clampInlineTextPx(pxValue);
      } else {
        const numericValue = parseNumeric(rawValue);
        if (numericValue !== null) parsed.sizeEm = clampInlineTextScale(numericValue);
      }
      return;
    }

    if (normalizedKey === 'b' || normalizedKey === 'bold') {
      parsed.bold = normalizedValue !== '0' && normalizedValue !== 'false' && normalizedValue !== 'no';
      return;
    }

    if (normalizedKey === 'i' || normalizedKey === 'italic') {
      parsed.italic = normalizedValue !== '0' && normalizedValue !== 'false' && normalizedValue !== 'no';
      return;
    }

    if (normalizedKey === 'c' || normalizedKey === 'color') {
      if (SAFE_COLOR_REGEX.test(rawValue)) parsed.color = rawValue;
      return;
    }
  });

  return parsed;
};

export const parseInlineTextWithImages = (value: string | undefined): InlineImageTextSegment[] => {
  const source = typeof value === 'string' ? value : '';
  if (!source) return [{ type: 'text', value: '' }];

  const segments: InlineImageTextSegment[] = [];
  let cursor = 0;

  while (cursor < source.length) {
    INLINE_MARKDOWN_IMAGE_REGEX.lastIndex = cursor;
    INLINE_SIZED_TEXT_REGEX.lastIndex = cursor;

    const imageMatch = INLINE_MARKDOWN_IMAGE_REGEX.exec(source);
    const sizedTextMatch = INLINE_SIZED_TEXT_REGEX.exec(source);
    const nextMatch =
      imageMatch && sizedTextMatch
        ? (imageMatch.index <= sizedTextMatch.index
          ? { kind: 'image' as const, match: imageMatch }
          : { kind: 'sizedText' as const, match: sizedTextMatch })
        : imageMatch
          ? { kind: 'image' as const, match: imageMatch }
          : sizedTextMatch
            ? { kind: 'sizedText' as const, match: sizedTextMatch }
            : null;

    if (!nextMatch) break;

    const { kind, match } = nextMatch;
    const [token] = match;

    if (match.index > cursor) {
      segments.push({ type: 'text', value: source.slice(cursor, match.index) });
    }

    if (kind === 'image') {
      const rawMeta = match[1] || '';
      const rawUrl = match[2] || '';
      const cleanedUrl = normalizeInlineImageUrl(rawUrl);
      if (!isSafeInlineImageUrl(cleanedUrl)) {
        segments.push({ type: 'text', value: token });
      } else {
        const parsedMeta = parseInlineImageMeta(rawMeta);
        segments.push({
          type: 'image',
          url: cleanedUrl,
          alt: parsedMeta.alt,
          widthPx: parsedMeta.widthPx,
          heightPx: parsedMeta.heightPx,
          verticalOffsetEm: parsedMeta.verticalOffsetEm,
        });
      }
      cursor = match.index + token.length;
      continue;
    }

    const rawText = match[1] || '';
    const rawMeta = match[2] || '';
    const parsedMeta = parseInlineSizedTextMeta(rawMeta);
    segments.push({
      type: 'sizedText',
      value: rawText,
      sizeEm: parsedMeta.sizeEm,
      sizePx: parsedMeta.sizePx,
      bold: parsedMeta.bold,
      italic: parsedMeta.italic,
      color: parsedMeta.color,
    });
    cursor = match.index + token.length;
  }

  if (cursor < source.length) {
    segments.push({ type: 'text', value: source.slice(cursor) });
  }

  return segments.length > 0 ? segments : [{ type: 'text', value: source }];
};

const renderTextWithLineBreaks = (
  textValue: string,
  textKey: string,
  style?: React.CSSProperties,
  className?: string,
): React.ReactNode => {
  if (!textValue.includes('\n')) {
    if (style || className) {
      return (
        <span key={textKey} style={style} className={className}>
          {textValue}
        </span>
      );
    }
    return <React.Fragment key={textKey}>{textValue}</React.Fragment>;
  }

  const lines = textValue.split('\n');
  return (
    <React.Fragment key={textKey}>
      {lines.map((line, lineIndex) => (
        <React.Fragment key={`${textKey}-line-${lineIndex}`}>
          {(style || className) ? <span style={style} className={className}>{line}</span> : line}
          {lineIndex < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      ))}
    </React.Fragment>
  );
};

export const renderInlineTextWithImages = (
  value: string | undefined,
  keyPrefix: string,
  defaultHeightEm: number = 1,
): React.ReactNode => {
  const segments = parseInlineTextWithImages(value);

  return segments.map((segment, segmentIndex) => {
    if (segment.type === 'text') {
      return renderTextWithLineBreaks(segment.value, `${keyPrefix}-text-${segmentIndex}`);
    }
    if (segment.type === 'sizedText') {
      const sizedTextStyle: React.CSSProperties = {
        fontSize: segment.sizePx !== undefined
          ? `${segment.sizePx}px`
          : (segment.sizeEm !== undefined ? `${segment.sizeEm}em` : undefined),
        lineHeight: 'inherit',
        fontWeight: segment.bold ? 700 : undefined,
        fontStyle: segment.italic ? 'italic' : undefined,
        color: segment.color || undefined,
      };
      return renderTextWithLineBreaks(
        segment.value,
        `${keyPrefix}-sized-text-${segmentIndex}`,
        sizedTextStyle,
        'storefront-inline-sized-text',
      );
    }

    return (
      <img
        key={`${keyPrefix}-img-${segmentIndex}`}
        src={segment.url}
        alt={segment.alt}
        style={{
          display: 'inline-block',
          verticalAlign: segment.verticalOffsetEm !== undefined ? `${segment.verticalOffsetEm}em` : '0.1em',
          objectFit: 'contain',
          margin: '0 0.08em',
          height: segment.heightPx ? `${segment.heightPx}px` : `${defaultHeightEm}em`,
          width: segment.widthPx ? `${segment.widthPx}px` : 'auto',
          maxWidth: '100%',
        }}
      />
    );
  });
};

export const toPlainInlineText = (value: string | undefined): string =>
  parseInlineTextWithImages(value)
    .map((segment) => {
      if (segment.type === 'text' || segment.type === 'sizedText') return segment.value;
      return segment.alt || '';
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
